import {
  Box,
  Container,
  IllustrationProps,
  Logo,
  Stack,
  Typography,
  useTheme,
  TypographyProps
} from "@carrotfertility/carotene-core"
import { UnauthPreferredLocale } from "#/components/views/UnauthPreferredLocale"
import React, { ReactElement, ReactNode } from "react"

interface RegistrationFlowLayoutProps {
  header?: ReactNode | ReactNode[]
  headerVariant?: TypographyProps["variant"]
  description?: ReactNode | ReactNode[]
  interactiveElements?: ReactNode | ReactNode[]
  imageName?: string
  illustration?: ReactElement<IllustrationProps>
  showLocaleSelect?: boolean
  employeeId: number
}

export function RegistrationFlowLayout({ ...props }: RegistrationFlowLayoutProps): JSX.Element {
  const showLocaleSelect = props.showLocaleSelect
  const theme = useTheme()

  return (
    <Box
      bgcolor="#DEF2F8"
      height="100%"
      width="100%"
      minHeight="100vh"
      paddingBottom={(theme) => ({ xs: 0, md: theme.spacing(theme.tokens.spacing.xxl) })}
    >
      <Box
        display={{ xs: "none", md: "block" }}
        padding={(theme) => theme.spacing(theme.tokens.spacing.xxl, theme.tokens.spacing.xxxl)}
      >
        <Logo variant="name" color="black" sx={{ inlineSize: "120px" }} />
      </Box>
      <Container
        component="main"
        maxWidth={false}
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          border: `${theme.tokens.borderWidth.sm} solid white`,
          borderRadius: theme.tokens.borderRadius.md,
          minBlockSize: "100vh",
          maxInlineSize: 624,
          [theme.breakpoints.down("md")]: {
            padding: theme.spacing(theme.tokens.spacing.md)
          },
          [theme.breakpoints.up("md")]: {
            padding: theme.spacing(theme.tokens.spacing.xl),
            minBlockSize: "unset"
          }
        })}
      >
        <Stack spacing={theme.tokens.spacing.xl} alignContent="flex-start">
          {showLocaleSelect && <UnauthPreferredLocale showModal={true} employeeId={props.employeeId} />}
          <RegistrationStepLayout {...props} />
        </Stack>
      </Container>
    </Box>
  )
}

function RegistrationStepLayout({
  header,
  headerVariant = "h3",
  description,
  interactiveElements,
  imageName,
  illustration
}: RegistrationFlowLayoutProps) {
  return (
    <Box marginBottom={(theme) => theme.spacing(theme.tokens.spacing.md)}>
      <Box display="flex" flexDirection="column">
        <Box display="flex" justifyContent="center">
          {illustration ??
            (imageName ? (
              <Box
                component="img"
                src={`/images/${imageName}.png`}
                srcSet={`/images/${imageName}-2x.png`}
                alt=""
                marginTop={(theme) => theme.spacing(theme.tokens.spacing.xs)}
                marginBottom={(theme) => theme.spacing(theme.tokens.spacing.xxl)}
                height={228}
              />
            ) : (
              <Box marginBottom={(theme) => theme.spacing(theme.tokens.spacing.xs)} />
            ))}
        </Box>
        <Typography
          color={(theme) => theme.palette.text.primary}
          variant={headerVariant}
          component="h1"
          marginBottom={(theme) => theme.spacing(theme.tokens.spacing.xs)}
        >
          {header}
        </Typography>
        {description}
      </Box>
      {interactiveElements}
    </Box>
  )
}

RegistrationFlowLayout.defaultProps = {
  showLocaleSelect: true
}

interface RegistrationErrorLayoutProps {
  header: ReactNode
  description: ReactNode | ReactNode[]
  employeeId?: number
}

export function RegistrationErrorLayout({
  header,
  description,
  employeeId
}: RegistrationErrorLayoutProps): JSX.Element {
  const theme = useTheme()

  return (
    <Box bgcolor="#DEF2F8" height="100%" width="100%" minHeight="100vh" display="flex" justifyContent="center">
      <Box
        component="main"
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          border: `${theme.tokens.borderWidth.sm} solid white`,
          borderRadius: theme.tokens.borderRadius.md,
          maxInlineSize: 512,
          inlineSize: "100%",
          blockSize: "fit-content",
          marginBlockStart: theme.spacing(theme.tokens.spacing.xl),
          paddingBlockStart: theme.spacing(theme.tokens.spacing.xl),
          paddingBlockEnd: theme.spacing(theme.tokens.spacing.xl),
          paddingInlineStart: theme.spacing(theme.tokens.spacing.xxxl),
          paddingInlineEnd: theme.spacing(theme.tokens.spacing.xxxl)
        })}
      >
        <Stack spacing={theme.tokens.spacing.md}>
          <Box
            display="flex"
            justifyContent="center"
            sx={(theme) => ({
              paddingBlockEnd: theme.spacing(theme.tokens.spacing.lg),
              paddingBlockStart: theme.spacing(theme.tokens.spacing.xl)
            })}
          >
            <Logo variant="name" color="black" sx={{ inlineSize: "120px" }} />
          </Box>
          <Typography color={(theme) => theme.palette.text.secondary} variant="h1">
            {header}
          </Typography>
          <Box>{description}</Box>
          <Box display="flex" justifyContent="center">
            <UnauthPreferredLocale showModal={true} employeeId={employeeId} />
          </Box>
        </Stack>
      </Box>
    </Box>
  )
}
